import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

const PresetTemplates = ({ onApplyTemplate, onManualSetup, loading, disabled = false }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');

  const fetchTemplates = useCallback(async () => {
    try {
      setFetching(true);
      setError('');
      const response = await api.get('/api/grading-attendance-rules/templates/presets');
      
      if (response.data.success) {
        console.log('✅ Templates loaded successfully');
      } else {
        setError('Failed to load templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      if (error?.response?.status === 404) {
        setError('Templates endpoint not found. Please check backend setup.');
      } else {
        setError('Failed to load templates: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const templateInfo = {
    school_percentage: {
      name: 'CBSE School (Percentage)',
      description: 'For CBSE/ICSE schools with percentage system',
      icon: '🏫',
      details: {
        gradingType: 'percentage',
        ciePassing: '33%',
        endExamPassing: '33%',
        overallPassing: '33%',
        special: 'Must pass both components'
      }
    },
    university_grade: {
      name: 'University (Letter Grades)',
      description: 'Universities using O, A+, A, B+, B, C, D, E, F grades',
      icon: '🎓',
      details: {
        gradingType: 'grade',
        ciePassing: '40%',
        endExamPassing: '40%',
        overallPassing: '40%',
        special: 'O to F grade mapping with 10-point scale'
      }
    },
    engineering_cgpa: {
      name: 'Engineering College (CGPA)',
      description: 'AICTE approved engineering colleges with CGPA',
      icon: '⚙️',
      details: {
        gradingType: 'cgpa',
        ciePassing: '40%',
        endExamPassing: '40%',
        overallPassing: '40%',
        special: '10-point CGPA with AA to FF grades'
      }
    },
    training_pass_fail: {
      name: 'Training Center (Pass/Fail)',
      description: 'Simple pass/fail for certificate courses',
      icon: '📜',
      details: {
        gradingType: 'percentage',
        ciePassing: 'N/A',
        endExamPassing: '50%',
        overallPassing: '50%',
        special: 'End exam only, simple pass/fail'
      }
    },
    medical_college: {
      name: 'Medical College',
      description: 'Medical colleges with strict passing criteria',
      icon: '⚕️',
      details: {
        gradingType: 'percentage',
        ciePassing: '50%',
        endExamPassing: '50%',
        overallPassing: '50%',
        special: 'Higher passing threshold (50%)'
      }
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    if (disabled) {
      console.log('Apply template disabled - user ID not available');
      return;
    }
    const success = await onApplyTemplate(selectedTemplate);
    if (success) {
      setSelectedTemplate('');
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Choose a Template</h2>
          <p className="text-gray-600 mt-1">Select a pre-configured template for your institution type</p>
        </div>
        <button
          onClick={onManualSetup}
          className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Manual Setup
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {fetching && (
        <div className="mb-6 flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading templates...</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        {Object.entries(templateInfo).map(([key, info]) => (
          <div
            key={key}
            className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
              selectedTemplate === key 
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                : disabled 
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'
            }`}
            onClick={() => !disabled && setSelectedTemplate(key)}
          >
            <div className="flex items-start gap-4">
              <div className="text-3xl">{info.icon}</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg mb-1">{info.name}</h3>
                <p className="text-gray-500 text-sm mb-3">{info.description}</p>
                
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Grading:</span>
                    <span className="font-medium text-gray-700">{info.details.gradingType.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">CIE Pass:</span>
                    <span className="font-medium text-gray-700">{info.details.ciePassing}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">End Exam Pass:</span>
                    <span className="font-medium text-gray-700">{info.details.endExamPassing}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Overall Pass:</span>
                    <span className="font-medium text-gray-700">{info.details.overallPassing}</span>
                  </div>
                </div>
                
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <span className="text-xs text-blue-600">{info.details.special}</span>
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                selectedTemplate === key ? 'bg-blue-500 border-blue-500' : 
                disabled ? 'border-gray-300 bg-gray-200' : 'border-gray-300'
              }`}>
                {selectedTemplate === key && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-5 border-t border-gray-200">
        <div>
          {selectedTemplate ? (
            <span className="text-sm text-gray-600">
              Selected: <span className="font-semibold text-gray-900">{templateInfo[selectedTemplate]?.name}</span>
            </span>
          ) : (
            <span className="text-sm text-gray-400">Select a template to begin</span>
          )}
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onManualSetup}
            className="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors text-sm"
          >
            Start from Scratch
          </button>
          <button
            onClick={handleApplyTemplate}
            disabled={!selectedTemplate || loading || disabled}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Applying...
              </>
            ) : disabled ? (
              'Login Required'
            ) : (
              'Apply Template'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PresetTemplates;